import React, { useEffect, useRef, useState } from "react";
import {
  BarChart3,
  Users,
  ClipboardCheck,
  Check,
  Send,
  MessageSquare,
  FileText,
} from "lucide-react";
import { features } from "../mock";

const ORANGE = "#5BA4FB";

/* ---------- scroll reveal hook ---------- */
function useInView(threshold = 0.2) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ob = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setInView(true);
          ob.disconnect();
        }
      },
      { threshold }
    );
    ob.observe(el);
    return () => ob.disconnect();
  }, [threshold]);
  return [ref, inView];
}

/* ---------- nested dashed frame wrapper ---------- */
const Frame = ({ children }) => (
  <div className="relative mx-auto aspect-[4/5] w-full max-w-[440px] rounded-[34px] bg-[#E5EBF4]">
    <div className="absolute inset-5 rounded-[28px] border border-dashed border-slate-300/80" />
    <div className="absolute inset-10 rounded-[24px] border border-dashed border-slate-300/70" />
    <div className="absolute inset-16 rounded-[20px] border border-dashed border-slate-300/60" />
    <div className="absolute inset-0 flex items-center justify-center p-8">
      {children}
    </div>
  </div>
);

/* ---------- visuals ---------- */
const AnalyticsVisual = () => (
  <Frame>
    <div className="w-[58%] rounded-2xl bg-white p-6 shadow-[0_18px_50px_rgba(20,40,70,0.12)]">
      <div className="flex h-44 items-end justify-center gap-3">
        {[55, 95, 70, 38].map((h, i) => (
          <div
            key={i}
            className="w-6 rounded-full"
            style={{
              height: `${h}%`,
              background: `linear-gradient(to top, ${ORANGE}, #AECFFD)`,
              opacity: i === 1 ? 1 : 0.75,
            }}
          />
        ))}
      </div>
    </div>
  </Frame>
);

const CollabVisual = () => (
  <Frame>
    <div className="relative h-full w-full">
      <div className="absolute left-1/2 top-1/2 h-[78%] w-[78%] -translate-x-1/2 -translate-y-1/2 rounded-full border border-dashed border-slate-300/70" />
      <div className="absolute left-1/2 top-1/2 h-[52%] w-[52%] -translate-x-1/2 -translate-y-1/2 rounded-full border border-dashed border-slate-300/70" />
      {/* center */}
      <div
        className="absolute left-1/2 top-1/2 flex h-24 w-24 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full text-white shadow-[0_16px_40px_rgba(91,164,251,0.45)]"
        style={{ background: `linear-gradient(145deg, ${ORANGE}, #2F80E8)` }}
      >
        <Users className="h-9 w-9" />
      </div>
      {/* floating bubbles */}
      <span className="absolute right-[14%] top-[10%] flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-[0_10px_24px_rgba(0,0,0,0.10)]">
        <Send className="h-5 w-5" style={{ color: ORANGE }} />
      </span>
      <span className="absolute bottom-[26%] left-[8%] flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-[0_10px_24px_rgba(0,0,0,0.10)]">
        <MessageSquare className="h-5 w-5" style={{ color: ORANGE }} />
      </span>
      <span className="absolute bottom-[10%] right-[20%] flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-[0_10px_24px_rgba(0,0,0,0.10)]">
        <FileText className="h-5 w-5" style={{ color: ORANGE }} />
      </span>
    </div>
  </Frame>
);

const TaskVisual = () => (
  <Frame>
    <div className="relative flex h-44 w-36 items-center justify-center">
      {/* document */}
      <div className="absolute inset-0 rounded-2xl bg-white shadow-[0_18px_50px_rgba(20,40,70,0.12)]">
        <div className="absolute right-0 top-0 h-9 w-9 rounded-bl-xl rounded-tr-2xl bg-slate-100" />
      </div>
      {/* check */}
      <div
        className="relative flex h-16 w-16 items-center justify-center rounded-full text-white shadow-[0_14px_34px_rgba(91,164,251,0.45)]"
        style={{ background: `linear-gradient(145deg, ${ORANGE}, #2F80E8)` }}
      >
        <Check className="h-8 w-8" strokeWidth={3} />
      </div>
    </div>
  </Frame>
);

const badgeIcon = {
  analytics: <BarChart3 className="h-6 w-6 text-white" />,
  collab: <Users className="h-6 w-6 text-white" />,
  task: <ClipboardCheck className="h-6 w-6 text-white" />,
};

const visualFor = {
  analytics: <AnalyticsVisual />,
  collab: <CollabVisual />,
  task: <TaskVisual />,
};

/* ---------- single feature row ---------- */
const FeatureRow = ({ feature }) => {
  const [ref, inView] = useInView(0.18);
  const { reverse } = feature;

  const Text = (
    <div className="flex-1">
      <div
        className="flex h-14 w-14 items-center justify-center rounded-2xl shadow-[0_10px_24px_rgba(91,164,251,0.35)]"
        style={{ background: `linear-gradient(145deg, ${ORANGE}, #2F80E8)` }}
      >
        {badgeIcon[feature.badge]}
      </div>

      <h3 className="mt-8 text-4xl font-bold tracking-tight text-[#16263F] md:text-5xl">
        {feature.title}
      </h3>
      <p className="mt-6 max-w-md text-[15px] leading-7 text-slate-400">
        {feature.description}
      </p>

      <ul className="mt-8 space-y-5">
        {feature.bullets.map((b) => (
          <li key={b} className="flex items-center gap-3">
            <span
              className="flex h-6 w-6 items-center justify-center rounded-full"
              style={{ background: "rgba(91,164,251,0.12)" }}
            >
              <Check className="h-4 w-4" style={{ color: ORANGE }} strokeWidth={3} />
            </span>
            <span className="text-[17px] font-medium text-[#2a3f5f]">{b}</span>
          </li>
        ))}
      </ul>
    </div>
  );

  const Visual = <div className="flex-1">{visualFor[feature.key]}</div>;

  return (
    <div
      ref={ref}
      className={`reveal ${inView ? "in" : ""} border-t border-slate-200/80 py-20`}
    >
      <div
        className={`mx-auto flex max-w-[1180px] flex-col items-center gap-14 px-6 md:flex-row md:gap-20 ${
          reverse ? "md:flex-row-reverse" : ""
        }`}
      >
        {Text}
        {Visual}
      </div>
    </div>
  );
};

const FeaturesSection = () => (
  <section id="features" className="bg-[#EDF1F8]">
    {features.map((f) => (
      <FeatureRow key={f.key} feature={f} />
    ))}
  </section>
);

export default FeaturesSection;
